/* eslint-disable react/no-array-index-key */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useExcelReader } from "../../file-reader";
import { Button, Modal } from "../../components/tailwind";
import { cellIsHardNoTranslate, updateInputOnFocus } from "./utils";
import { useNoTranslateCells } from "./useNoTranslateCells";

import { RowRender } from "./RowRender";
import { LetterHeaderRowRender } from "./LetterHeaderRowRender";
import { Toolbar } from "./Toolbar";
import { Spinner } from "../../utils/Spinner";
import { ExcelFileViewerMode, NoTranslateCell } from "./types";
import { Row } from "exceljs";

type Props = {
  file: File;
  nextAction: (noTranslateCells: Omit<NoTranslateCell, "locked">[]) => void;
  open: boolean;
  mode?: ExcelFileViewerMode;
  onClose?: () => void;
  LanguageSelector?: React.FC<{
    cellNumber: number;
  }>;
  allowAddColumns?: boolean;
  defaultExtraColumns?: number;
  readHeadersCallback?: (headers: Row) => void;
};

export const ExcelFileViewer: React.FC<Props> = ({
  file,
  nextAction,
  open,
  mode = "edit",
  onClose,
  LanguageSelector,
  allowAddColumns = false,
  defaultExtraColumns = 0,
  readHeadersCallback,
}) => {
  const excelReader = useExcelReader();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const abortController = useRef<AbortController>(new AbortController());
  const [focusedCell, setFocusedCell] = useState<{
    row: number;
    column: number;
  } | null>(null);

  const [extraColumns, setExtraColumns] = useState<number>(defaultExtraColumns);

  const columns = useMemo(() => {
    const excelColumns = excelReader.currentSheet?.actualColumnCount;
    if (!excelColumns) return extraColumns;
    return excelColumns + extraColumns;
  }, [excelReader.currentSheet?.actualColumnCount, extraColumns]);

  const tableLayout = useMemo(() => {
    const rows = excelReader.currentSheet?.actualRowCount ?? 0;

    return {
      columns,
      rows: rows,
    };
  }, [excelReader.currentSheet, columns]);

  const noTranslateCells = useNoTranslateCells(
    excelReader.currentSheet?.actualRowCount ?? 0,
    columns
  );

  const getFocusedCell = useCallback(() => {
    const focusedCell = document.querySelector<HTMLTableCellElement>(
      "td:focus"
    );
    if (!focusedCell) {
      return null;
    }
    const row = focusedCell.closest<HTMLTableRowElement>("tr")?.rowIndex;
    const column = focusedCell.cellIndex;
    setFocusedCell({ row, column });
    return { row, column };
  }, []);

  useEffect(() => {
    dialogRef.current?.addEventListener(
      "close",
      () => {
        onClose?.();
      },
      {
        signal: abortController.current.signal,
      }
    );
    document.addEventListener("focusin", getFocusedCell, {
      signal: abortController.current.signal,
    });
    document.addEventListener("focusin", updateInputOnFocus, {
      signal: abortController.current.signal,
    });
    return () => {
      abortController.current.abort();
    };
  }, []);

  useEffect(() => {
    if (file) {
      if (open) {
        excelReader.readFile(file);
        dialogRef.current?.showModal();
      } else {
        dialogRef.current?.close();
      }
    }
  }, [file, open]);

  useEffect(() => {
    if (!excelReader.isLoading && excelReader.workbook) {
      const rows = excelReader.rows;
      const headers = excelReader.headers;
      const columnsIsHardNoTranslate: number[] = [];
      headers.eachCell((cell) => {
        if (cellIsHardNoTranslate(cell)) {
          noTranslateCells.markCellAsNoTranslateInFile({
            row: cell.fullAddress.row,
            column: cell.fullAddress.col,
          });
          columnsIsHardNoTranslate.push(cell.fullAddress.col);
        }
      });
      for (const row of rows) {
        row.eachCell((cell) => {
          if (
            cellIsHardNoTranslate(cell) ||
            columnsIsHardNoTranslate.includes(cell.fullAddress.col)
          ) {
            noTranslateCells.markCellAsNoTranslateInFile({
              row: cell.fullAddress.row,
              column: cell.fullAddress.col,
            });
          }
        });
      }
      readHeadersCallback?.(headers);
    }
  }, [excelReader.isLoading, excelReader.workbook, extraColumns]);

  const isAnalyzing = excelReader.isLoading;

  const noWorkbook = !excelReader.workbook && !isAnalyzing;
  const shouldRenderBody = excelReader.workbook && !isAnalyzing;

  const allCellsAreMarkedAsNoTranslate = useMemo(() => {
    const cellsNotInHeader = noTranslateCells.cells.filter(
      (cell) => cell.row !== 1
    );
    const allCells = (tableLayout.rows - 1) * tableLayout.columns;
    return cellsNotInHeader.length === allCells;
  }, [noTranslateCells.cells, tableLayout.rows, tableLayout.columns]);

  const collectAndCommit = (): void => {
    const cells = noTranslateCells.cells.map((cell) => ({
      row: cell.row,
      column: cell.column,
    }));
    abortController.current.abort();
    nextAction(cells);
  };
  return (
    <Modal
      dialogRef={dialogRef}
      size="full"
      className="tw-overflow-hidden"
      contentClassName="tw-h-full tw-w-full"
    >
      <Modal.Header divide>
        <h2 data-testid="excel-file-name">{file?.name}</h2>
      </Modal.Header>
      <Modal.Body className="tw-w-full tw-h-full !tw-pb-72">
        {noWorkbook && <div data-testid="no-workbook-message">No workbook</div>}
        {isAnalyzing && (
          <div
            className="tw-flex tw-flex-col tw-gap-2 tw-h-full tw-w-full tw-items-center tw-justify-center"
            data-testid="analyzing-file"
          >
            <Spinner />{" "}
            <span className="tw-animate-pulse">
              Analyzing File <b>{file?.name}</b>...
            </span>
          </div>
        )}
        {excelReader.error && (
          <div data-testid="error-message">
            Error: {excelReader.error.message}
          </div>
        )}
        {shouldRenderBody && (
          <>
            <small data-testid="header-info">
              If needed, you can select cells and columns as No Translate.
              <br />
              Select column/cell and press the “lock icon”.
              <br />
              <b>Row 1</b> is treated as header and will not be translated.
            </small>
            <br />
            <small
              className="tw-flex tw-items-center tw-gap-1"
              data-testid="no-translate-info"
            >
              Cells with style{" "}
              <span className="tw-bg-red-50 tw-border tw-border-red-500 tw-h-4 tw-w-4 tw-inline-block"></span>
              are marked as <b>No translate</b> (<code>***</code>)
            </small>
            <Toolbar
              focusedCell={focusedCell}
              noTranslateCells={noTranslateCells}
              mode={mode}
            />
            {allowAddColumns && (
              <div className="tw-flex tw-justify-end">
                <Button
                  size="sm"
                  type="button"
                  variant="primary-alt"
                  onClick={() => setExtraColumns((prev) => prev + 1)}
                >
                  Add New Column
                </Button>
              </div>
            )}

            <div
              className="txu-excel-file-viewer tw-py-4"
              data-testid="excel-table"
            >
              <table className="tw-w-full">
                <tbody>
                  <LetterHeaderRowRender
                    numberOfCells={tableLayout.columns}
                    noTranslateGetColumn={noTranslateCells.getColumn}
                    mode={mode}
                    LanguageSelector={LanguageSelector}
                  />
                  {Array.from({ length: tableLayout.rows }).map((_, index) => (
                    <RowRender
                      key={index}
                      rowNumber={index + 1}
                      numberOfCells={tableLayout.columns}
                      excelReader={excelReader}
                      noTranslateCellInfo={noTranslateCells.getCell}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Actions>
        <Button
          className="tw-mt-auto"
          size="sm"
          type="button"
          onClick={() => {
            dialogRef.current?.close();
          }}
          data-testid="close-button"
        >
          Close
        </Button>
        {mode === "edit" && (
          <div className="tw-flex tw-flex-col tw-gap-2">
            <small
              className={`tw-text-red-500 ${
                allCellsAreMarkedAsNoTranslate ? "tw-visible" : "tw-invisible"
              }`}
              data-testid="all-cells-no-translate-warning"
            >
              All Translatable cells are marked as <b>No translate</b>
            </small>
            <Button
              size="sm"
              type="button"
              variant="primary"
              onClick={collectAndCommit}
              disabled={allCellsAreMarkedAsNoTranslate}
              data-testid="next-button"
            >
              Next
            </Button>
          </div>
        )}
      </Modal.Actions>
    </Modal>
  );
};
